import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Skeleton,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { Select } from 'chakra-react-select';
import { useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { nationalityOptions } from '@blockpulse3/data/shared';
import {
  IdentityVerificationStatus,
  UserRole,
  useGetUserByIndividualIdentityIdQuery,
} from '@blockpulse3/graphql/hooks';
import {
  ErrorMessage,
  InputImage,
  NotAllowedCard,
  UserIdentityAvatar,
  VerificationBadge,
  useErrorToast,
} from '@blockpulse3/ui/commons';

import { useUser } from '../../../providers';
import { IdentityVerification } from '../../IdentityVerification';
import { profileDefaultValues, schema } from './schema';
import { IUpdateProfileForm } from './types';

type Props = {
  defaultValues?: IUpdateProfileForm;
  onSubmit: (data: IUpdateProfileForm) => void;
  isVerified: boolean;
};

/**
 * UpdateProfileForm.
 * Form to update the profile infos of the user.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function UpdateProfileForm({
  defaultValues = profileDefaultValues,
  onSubmit,
  isVerified,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nUpdateProfileForm = useTranslations('UpdateProfileForm');
  const [isImageUploading, setIsImageUploading] = useState<boolean>(false);

  const { user: currentUser, loadUser } = useUser();
  const { individualIdentityId = '' } = useParams();

  const {
    data: individualUser,
    loading: loadingIndividualUser,
    refetch: refetchIndividualUser,
  } = useGetUserByIndividualIdentityIdQuery({
    variables: {
      individualIdentityId,
    },
    skip: !individualIdentityId,
  });

  const user = individualIdentityId ? individualUser?.getUserByIndividualIdentityId : currentUser;

  const refetch = (): void => {
    if (individualIdentityId) {
      refetchIndividualUser();
    } else {
      loadUser();
    }
  };

  const errorToast = useErrorToast();

  /* ** Control if form is "touched" (!= changed) ** */
  const [isTouched, setIsTouched] = useState<boolean>(false);
  /* ** Update profile form ** */
  const { register, control, formState, handleSubmit } = useForm<IUpdateProfileForm>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });

  /* ** Submit button disabled state ** */
  const isDisabled = useMemo(() => {
    return !formState.isValid || !isTouched;
  }, [isTouched, formState.isValid]);

  const verificationStatus = useMemo(() => {
    switch (user?.individualIdentity?.kycVerificationStatus) {
      case IdentityVerificationStatus.APPROVED:
        return 'valid';
      case IdentityVerificationStatus.PROCESSING:
        return 'in-progress';
      case IdentityVerificationStatus.PENDING:
        return 'pending';
      case IdentityVerificationStatus.REFUSED:
        return 'error-identity';
      default:
        return 'none';
    }
  }, [user?.individualIdentity]);

  if (
    individualIdentityId &&
    currentUser?.individualIdentity?.id !== individualIdentityId &&
    !currentUser?.roles.includes(UserRole.SUPERADMIN)
  ) {
    return <NotAllowedCard h="250px" />;
  }

  if (loadingIndividualUser) {
    return <Skeleton h="250px" />;
  }

  const handleFormTouched = (): void => {
    if (!isTouched) {
      setIsTouched(true);
    }
  };

  const handleFormSubmit: SubmitHandler<IUpdateProfileForm> = (data: IUpdateProfileForm) => {
    onSubmit(data);
  };

  const handleProfilePictureUpload = async (files: FileList): Promise<void> => {
    if (files.length > 0 && user?.individualIdentity?.identity?.id) {
      setIsImageUploading(true);

      const formData = new FormData();
      formData.append('identityId', user.individualIdentity.identity.id);
      formData.append('document', files[0]);

      await axios
        .post(
          process.env['NX_API_CONTROLLER_ENDPOINT'] + '/identities/uploadProfilePicture',
          formData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        )
        .then(() => {
          refetch();
          setIsImageUploading(false);
        })
        .catch(() => {
          errorToast({ title: i18nUpdateProfileForm('Toast.UploadError') });
          setIsImageUploading(false);
        });
    }
  };

  return (
    <form onChange={handleFormTouched} onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: '5', lg: '8' }}>
        <Stack
          alignItems={{ base: 'center', lg: 'flex-start' }}
          direction={{ base: 'row', lg: 'column' }}
          flexBasis={{ lg: 250 }}
        >
          <Text fontSize="lg" fontWeight="medium">
            {i18nUpdateProfileForm('FormTitle')}
          </Text>
          {isVerified && (
            <VerificationBadge status="valid" title={i18nUpdateProfileForm('ProfileVerified')} />
          )}
        </Stack>
        <Card maxW={{ lg: '3xl' }} variant="divider-bottom" w="full">
          <CardBody>
            <Stack spacing="6">
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors?.profilePicture}>
                  <FormLabel>{i18nUpdateProfileForm('ProfilePictureField')}</FormLabel>
                  <HStack spacing="4">
                    <Box position="relative">
                      {isImageUploading && (
                        <Spinner
                          color="whiteAlpha.700"
                          h="50%"
                          left="25%"
                          position="absolute"
                          speed="1s"
                          thickness="4px"
                          top="25%"
                          w="50%"
                          zIndex="1"
                        />
                      )}
                      <UserIdentityAvatar
                        boxSize="16"
                        src={user?.individualIdentity?.identity?.profilePicture}
                      />
                    </Box>
                    <InputImage<IUpdateProfileForm>
                      control={control}
                      isDisabled={isImageUploading}
                      name="profilePicture"
                      onUpload={handleProfilePictureUpload}
                    />
                  </HStack>
                </FormControl>
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <Controller
                  control={control}
                  name="gender"
                  render={({ field }): JSX.Element => (
                    <FormControl isInvalid={!!formState.errors?.gender}>
                      <FormLabel htmlFor="gender">{i18nUpdateProfileForm('GenderLabel')}</FormLabel>
                      <RadioGroup defaultValue={defaultValues.gender} id="gender" {...field}>
                        <Stack
                          direction={{ base: 'column', md: 'row' }}
                          spacing={{ base: 2, md: 4 }}
                        >
                          <Radio value="M">{i18nUpdateProfileForm('Mr')}</Radio>
                          <Radio value="F">{i18nUpdateProfileForm('Mrs')}</Radio>
                        </Stack>
                      </RadioGroup>
                      <ErrorMessage error={formState.errors?.gender} />
                    </FormControl>
                  )}
                />
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors?.firstName}>
                  <FormLabel htmlFor="firstName">
                    {i18nUpdateProfileForm('FirstNameField')}
                  </FormLabel>
                  <Input
                    id="firstName"
                    isDisabled={isVerified}
                    type="text"
                    {...register('firstName')}
                  />
                  <ErrorMessage error={formState.errors?.firstName} />
                </FormControl>
                <FormControl isInvalid={!!formState.errors?.lastName}>
                  <FormLabel htmlFor="lastName">{i18nUpdateProfileForm('LastNameField')}</FormLabel>
                  <Input
                    id="lastName"
                    isDisabled={isVerified}
                    type="text"
                    {...register('lastName')}
                  />
                  <ErrorMessage error={formState.errors?.lastName} />
                </FormControl>
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl isInvalid={!!formState.errors.birthdate}>
                  <FormLabel htmlFor="lastName">
                    {i18nUpdateProfileForm('BirthdateField')}
                  </FormLabel>
                  <Input
                    id="birthdate"
                    type="date"
                    {...register('birthdate')}
                    isDisabled={isVerified}
                  />
                  <ErrorMessage error={formState.errors?.birthdate} />
                </FormControl>
                <Controller
                  control={control}
                  name="nationality"
                  render={({ field: { onChange, ...rest } }): JSX.Element => (
                    <FormControl isInvalid={!!formState.errors?.nationality}>
                      <FormLabel htmlFor="nationality">
                        {i18nUpdateProfileForm('NationalityField')}
                      </FormLabel>
                      <Select
                        id="nationality"
                        isDisabled={isVerified}
                        options={nationalityOptions}
                        onChange={(value): void => {
                          onChange(value);
                          handleFormTouched();
                        }}
                        {...rest}
                      />
                    </FormControl>
                  )}
                />
              </Stack>
              <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                <FormControl
                  isInvalid={!!formState.errors?.birthplace}
                  w={{ base: 'full', md: '49%' }}
                >
                  <FormLabel htmlFor="birthplace">
                    {i18nUpdateProfileForm('BirthCityField')}
                  </FormLabel>
                  <Input
                    id="birthplace"
                    type="text"
                    {...register('birthplace')}
                    isDisabled={isVerified}
                  />
                  <ErrorMessage error={formState.errors?.birthplace} />
                </FormControl>
              </Stack>
            </Stack>
          </CardBody>
          <Divider />
          <CardFooter justifyContent="flex-end">
            <IdentityVerification
              isUserWorkspace={true}
              refetchData={refetch}
              status={verificationStatus}
              isDisabled={
                !!individualIdentityId &&
                currentUser?.individualIdentity?.id !== individualIdentityId
              }
            />
          </CardFooter>
          <Divider />
          <CardFooter justifyContent="flex-end">
            <Button disabled={isDisabled} type="submit">
              {i18nGeneral('Submit')}
            </Button>
          </CardFooter>
        </Card>
      </Stack>
    </form>
  );
}

export type UpdateProfileFormProps = Props;
